import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";

// `capability` names a boolean the API computes on the user (e.g.
// can_publish_org_content). It widens access beyond `roles`, so a page whose
// API accepts more than one role does not turn people away at the door.
export default function ProtectedRoute({ children, roles, capability }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground text-sm">{t("common.loading")}</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // New sign-ups (e.g. Google) who haven't chosen a role yet must finish onboarding
  // before reaching any protected page — otherwise refreshing would skip the chooser.
  if (user.onboarded === false && location.pathname !== "/welcome") {
    return <Navigate to="/welcome" replace />;
  }

  const byRole = !roles || roles.includes(user.role) || user.role === "admin";
  if (!byRole && !(capability && user[capability])) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="unauthorized">
        <h1 className="text-3xl font-display font-semibold mb-3">{t("common.unauthorized_title")}</h1>
        <p className="text-muted-foreground">{t("common.unauthorized_sub")}</p>
      </div>
    );
  }
  return children;
}
